import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Layers, Radio, Trophy, Check, Mail, Star, MessageCircle, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATS = [
  { v: "240+", l: "Live deals per month" },
  { v: "£2.4B+", l: "Facilitated to date" },
  { v: "60+", l: "Panel members" },
  { v: "24hr", l: "Avg. bid-to-award" },
];

const FEATURES = [
  {
    icon: <Radio className="h-6 w-6" strokeWidth={2.2} />,
    title: "Redacted deal cards",
    body: "See every live deal's amount, term, purpose, sector, turnover band and trading years — no PII, no fluff, no CV chasing.",
  },
  {
    icon: <Layers className="h-6 w-6" strokeWidth={2.2} />,
    title: "Whole-panel bidding",
    body: "Submit APR, arrangement fee and terms in 30 seconds. Update your bid at any time until the deal is awarded.",
  },
  {
    icon: <Trophy className="h-6 w-6" strokeWidth={2.2} />,
    title: "Live leaderboard",
    body: "Watch competing bids appear in real-time (anonymised). Sharpen your rate to move up the board and win the deal.",
  },
  {
    icon: <Zap className="h-6 w-6" strokeWidth={2.2} />,
    title: "Same-day awards",
    body: "Most deals awarded within 24 hours. Winner gets applicant details, contract pack and drawdown coordination.",
  },
];

const TICKS = [
  "£5k to £500M deal flow",
  "17 funding purposes across 12 sectors",
  "Zero cost to join · no monthly fees",
  "Free to bid on any live deal",
  "Only pay a success fee on won deals",
  "Dedicated panel support desk",
];

const CASE_STUDIES = [
  {
    company: "Meridian Business Capital",
    focus: "Working Capital · £25k–£500k · UK-wide",
    quote: "We shifted the majority of our new-business enquiries onto SwiftCash inside three months. The redacted deal cards let us price fast — and only pay when we win. It's changed the economics of the whole book.",
    author: "David Fraser, Head of Origination",
    winRate: "38%",
    tickets: "£25–420k",
    avg: "£142k",
    story: [
      "Meridian is a mid-tier working-capital lender based in Nottingham, funding UK SMEs from £25k to £500k over 6–36 months. Before joining the SwiftCash panel, their new-business team was spending 65% of their time on unqualified leads sourced from broker aggregators, cold email, and event follow-ups. Conversion sat at 4%.",
      "In their first month on SwiftCash, Meridian bid on 42 live deals. They won 11 of them — a 26% win rate that climbed to 38% by month three as they got sharper at reading the redacted deal cards and setting their APR appetite by sector. Their average ticket sits at £142k, right in the middle of their scorecard.",
      "David Fraser, Head of Origination, says the real change is economic: \"Before, we were paying for the top of the funnel — leads, list rentals, our BDMs' time — regardless of whether we won a single ticket. On SwiftCash there's no listing fee and no monthly, we only pay when we book a deal. Our cost per funded pound has collapsed by 60%.\"",
      "The leaderboard visibility helps too. Meridian's underwriters can see they're 20–30 bps off the market rate on a given deal card, sharpen the offer inside the working day, and move up. On mid-ticket working capital deals — the sweet spot — Meridian is now consistently in the top 3 bidders.",
      "In quarter one this year, Meridian funded 47 tickets through the SwiftCash panel worth £6.7M in aggregate. Their BDM team has been redeployed onto relationship and repeat business, and their new-business acquisition cost has fallen 62% year-on-year.",
    ],
  },
  {
    company: "North Pier Asset Finance",
    focus: "Asset & Vehicle Finance · £15k–£2M",
    quote: "The live leaderboard is the killer feature. We can see we're 20 bps off the pace and sharpen our rate in real time. Won 14 tickets last quarter — every one was on our terms and inside our target margin.",
    author: "Priya Sharma, Sales Director",
    winRate: "31%",
    tickets: "£40k–1.6M",
    avg: "£310k",
    story: [
      "North Pier funds asset and vehicle finance deals from £15k to £2M across the UK — everything from van fleets to industrial CNC machinery to yellow-plant. As a specialist lender competing against the big high-street asset finance houses, their unit economics only work when they can bid quickly and sharply on the right kind of deal.",
      "Priya Sharma, Sales Director, joined North Pier in 2023 with a mandate to grow the book without inflating the sales team. \"We're a 14-person shop competing with lenders that have 400 BDMs. We had to find a channel that gave us qualified pre-screened deal flow at scale, without a matching headcount investment. SwiftCash is exactly that.\"",
      "The team joined the panel in early 2025 and now averages 30–40 active bids at any time. What separates SwiftCash from every other broker channel they've tested, Priya says, is the live leaderboard. \"We can see we're 20 bps off the pace on a Volvo tractor unit deal — sharpen the offer inside 10 minutes, and be back in front. That's a level of visibility we've never had before. It stops us over-pricing but also stops us leaving money on the table.\"",
      "In Q4 last year, North Pier won 14 tickets on SwiftCash — every single one at their target 4.5% net margin or better. Total book value: £4.3M. Half of those tickets came from repeat introducers on the panel who now see North Pier as their go-to for used commercial vehicles and heavy plant.",
      "Priya's team has grown by two people this year, funded entirely by the marginal profit from the SwiftCash channel. \"We treat it as a scalable origination engine now — not a broker relationship. It's the single most productive channel we run.\"",
    ],
  },
  {
    company: "Coastline Growth Partners",
    focus: "Growth Capital · £500k–£25M · Ambitious SMEs",
    quote: "Getting sector-qualified deal flow at the £1M+ level used to need an army of BDMs. SwiftCash delivers 15–20 vetted deals a month straight to our screen. Two acquisitions closed already this quarter.",
    author: "Marcus Whittle, Managing Partner",
    winRate: "22%",
    tickets: "£750k–6.4M",
    avg: "£2.1M",
    story: [
      "Coastline Growth Partners is a specialist growth-capital and acquisition-finance house funding UK SMEs from £500k to £25M. Their typical deal is a management buy-out, add-on acquisition, or growth-stage refinance for a business turning over £5–100M. Before SwiftCash, sourcing was almost entirely relationship-driven: private equity introducers, advisor networks, and cold outreach from three senior BDMs.",
      "The problem, says Managing Partner Marcus Whittle, was throughput. \"At our ticket size, deal quality matters more than quantity — but you still need pipeline. Our three BDMs were producing 4–6 real conversations a month between them, and only 40% of those were structurally fundable. SwiftCash gave us leverage we couldn't buy any other way.\"",
      "Coastline joined the SwiftCash panel in late 2024 and immediately opted into the £1M+ deal-flow tier. Since then they've received 15–20 vetted growth-capital deal cards per month, filtered by sector appetite, ticket size, and security profile. Their win rate sits at 22% — modest on paper, but in a segment where average deal value is £2.1M, that translates to serious book growth.",
      "\"The redacted cards are exactly what we need,\" Marcus adds. \"We can see turnover band, sector, existing debt band, purpose — enough to run our initial screen without wasting anyone's time. If it clears our filters, we bid. If not, we skip. Zero BDM hours wasted on unqualified leads.\"",
      "In the first quarter of this year, Coastline closed two acquisition-finance deals sourced entirely through SwiftCash — one hospitality group MBO and one healthcare add-on — worth £8.9M combined. Both are performing above underwriting model. \"For a five-year-old fund still building brand,\" Marcus notes, \"SwiftCash has been transformative. It punches above its weight in a space we thought was un-scalable.\"",
    ],
  },
];

export default function LenderLanding() {
  useSeo({
    title: "Join the SwiftCash Lender Panel — Live UK Deal Flow",
    description: "Bid on live UK business finance deals in real time. £5k to £500M pre-qualified deal flow, redacted deal cards, whole-panel bidding, only pay on wins.",
    path: "/lenders",
  });
  const [openIdx, setOpenIdx] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative hero-grad-dark text-white overflow-hidden">
        <div className="absolute inset-0 dots-dark opacity-40 pointer-events-none" />
        <div className="absolute -top-40 right-1/4 h-[500px] w-[500px] bg-emerald/12 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 pl-1 pr-4 py-1 rounded-full bg-white/5 border border-white/10 mb-7">
            <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-emerald text-white text-[10.5px] font-extrabold uppercase tracking-[0.08em]">
              <ShieldCheck className="h-3 w-3" strokeWidth={2.6} /> Lender panel
            </span>
            <span className="text-[12.5px] font-bold text-white/70">Whole-of-market · UK business finance</span>
          </div>

          <h1 className="font-display font-extrabold text-[46px] sm:text-[64px] lg:text-[84px] leading-[1] tracking-[-0.035em] max-w-4xl">
            Live UK deal flow, <br />
            <span className="text-emerald">bid in real-time.</span>
          </h1>

          <p className="mt-8 text-[17px] md:text-[19px] leading-[1.55] text-white/75 max-w-2xl font-medium">
            SwiftCash routes <b className="text-white">£5k to £500M</b> business finance applications to a curated UK panel. See redacted deal cards, bid your best rate, watch the leaderboard, win the deals that fit your book.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
            <Link to="/lender/signup" data-testid="lender-landing-signup"
              className="btn-shine group inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-emerald text-white font-extrabold text-[15px] hover:bg-emerald-2 shadow-emerald">
              Apply to join the panel
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
            <Link to="/lender/login" data-testid="lender-landing-login"
              className="inline-flex items-center gap-2 h-14 px-7 rounded-xl border border-white/20 text-white font-bold text-[14px] hover:bg-white/5">
              Existing lender · Log in
            </Link>
            <button
              type="button"
              onClick={() => setChatOpen(true)}
              data-testid="lender-landing-chat"
              className="inline-flex items-center gap-2 h-14 px-7 rounded-xl border border-emerald/30 bg-emerald/10 text-emerald font-bold text-[14px] hover:bg-emerald/20"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} /> Prefer a chat first?
            </button>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl pt-8 border-t border-white/10">
            {STATS.map((s) => (
              <div key={s.l} data-testid={`lender-stat-${s.l.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="font-display font-extrabold text-[28px] md:text-[38px] leading-none tracking-[-0.04em]">{s.v}</div>
                <div className="text-[12px] text-white/60 mt-2 font-semibold leading-tight">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="py-24 md:py-32 bg-paper-warm">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.2em] font-bold mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> How the panel works
            </div>
            <h2 className="font-display font-extrabold text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.035em] text-navy">
              Deal flow without <br />
              <span className="text-emerald">the sales chatter.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`lender-feature-${i + 1}`}
                className="rounded-[20px] bg-white border border-line p-7 md:p-8 hover:border-emerald/40 hover:-translate-y-1 transition-all duration-300">
                <div className="h-14 w-14 rounded-xl bg-emerald-soft text-emerald-3 flex items-center justify-center mb-6">
                  {f.icon}
                </div>
                <h3 className="font-display font-extrabold text-[22px] leading-[1.15] tracking-[-0.02em] text-navy mb-3">{f.title}</h3>
                <p className="text-[14.5px] leading-[1.6] text-navy/65 font-medium">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="py-24 md:py-32 bg-paper-warm">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.2em] font-bold mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Panel case studies
            </div>
            <h2 className="font-display font-extrabold text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.035em] text-navy">
              Real lenders. <br />
              <span className="text-emerald">Real numbers.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {CASE_STUDIES.map((c, i) => (
              <button key={i} data-testid={`case-study-${i + 1}`} onClick={() => setOpenIdx(i)}
                className="text-left rounded-[20px] bg-white border border-line p-7 md:p-8 hover:border-emerald/40 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full group">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-amber text-amber" />)}
                  </div>
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] font-extrabold text-navy/40 group-hover:text-emerald-3 transition-colors">
                    Read case study <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} />
                  </span>
                </div>
                <p className="text-[15px] leading-[1.6] text-navy/80 font-medium mb-8 flex-1 min-h-[190px]">
                  &ldquo;{c.quote}&rdquo;
                </p>
                <div className="pt-5 border-t border-line">
                  <div className="font-display font-extrabold text-[16px] text-navy tracking-[-0.015em] mb-1 min-h-[24px]">{c.company}</div>
                  <div className="text-[12px] text-navy/55 font-semibold mb-4 min-h-[36px] leading-[1.5]">{c.focus}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <MicroStat label="Win rate" value={c.winRate} />
                    <MicroStat label="Tickets" value={c.tickets} />
                    <MicroStat label="Avg deal" value={c.avg} />
                  </div>
                  <div className="mt-4 text-[11.5px] text-navy/50 font-mono uppercase tracking-[0.1em] font-bold">{c.author}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.2em] font-bold mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Panel terms
            </div>
            <h2 className="font-display font-extrabold text-[36px] md:text-[48px] leading-[1.02] tracking-[-0.035em] text-navy">
              Simple, straight, <br />
              <span className="text-emerald">skin in the game.</span>
            </h2>
            <p className="mt-6 text-[15.5px] text-navy/70 font-medium leading-[1.6]">
              We only make money when you make money. No listing fees, no monthly subscription, no commitment to bid.
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="grid sm:grid-cols-2 gap-3">
              {TICKS.map((t, i) => (
                <div key={i} data-testid={`lender-tick-${i}`}
                  className="flex items-start gap-3 rounded-xl border border-line bg-paper-warm p-4">
                  <span className="h-6 w-6 rounded-full bg-emerald flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                  </span>
                  <span className="text-[14px] text-navy font-semibold leading-[1.5]">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-grad-dark text-white py-20 md:py-28 overflow-hidden relative">
        <div className="absolute inset-0 dots-dark opacity-40 pointer-events-none" />
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 text-center relative">
          <h2 className="font-display font-extrabold text-[42px] md:text-[64px] leading-[0.98] tracking-[-0.04em]">
            Ready to see live UK <br />
            <span className="text-emerald">deal flow?</span>
          </h2>
          <p className="mt-6 text-[16px] text-white/70 max-w-xl mx-auto font-medium leading-[1.55]">
            Panel approval usually inside one working day. First live deals visible the moment you log in.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/lender/signup" data-testid="lender-cta-signup"
              className="btn-shine group inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-emerald text-white font-extrabold text-[15px] hover:bg-emerald-2 shadow-emerald">
              Apply to join the panel
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
            <Link to="/lender/login" data-testid="lender-cta-login"
              className="inline-flex items-center gap-2 h-14 px-7 rounded-xl border border-white/20 text-white font-bold text-[14px] hover:bg-white/5">
              Already on the panel · Log in
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Case-study deep-dive modal */}
      <Dialog open={openIdx !== null} onOpenChange={(o) => !o && setOpenIdx(null)}>
        <DialogContent className="max-w-3xl bg-white border-line rounded-[24px] p-0 overflow-hidden max-h-[88vh] flex flex-col" data-testid="case-study-modal">
          <VisuallyHidden>
            <DialogTitle>{openIdx !== null ? CASE_STUDIES[openIdx].company : "Case study"}</DialogTitle>
            <DialogDescription>Panel case study deep dive</DialogDescription>
          </VisuallyHidden>
          {openIdx !== null && (
            <>
              <div className="bg-navy text-white p-7 md:p-9 relative overflow-hidden shrink-0">
                <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-4">
                    <Trophy className="h-3 w-3" strokeWidth={2.6} /> Panel case study
                  </div>
                  <h2 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] leading-[1.1] text-white mb-2">
                    {CASE_STUDIES[openIdx].company}
                  </h2>
                  <div className="text-[13px] text-white/65 font-semibold">{CASE_STUDIES[openIdx].focus}</div>
                  <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 max-w-md">
                    <ModalStat label="Win rate" value={CASE_STUDIES[openIdx].winRate} />
                    <ModalStat label="Tickets" value={CASE_STUDIES[openIdx].tickets} />
                    <ModalStat label="Avg deal" value={CASE_STUDIES[openIdx].avg} />
                  </div>
                </div>
              </div>
              <div className="p-7 md:p-9 overflow-y-auto">
                <div className="rounded-xl bg-emerald-soft/60 border border-emerald/20 p-5 mb-7">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-amber text-amber" />)}
                  </div>
                  <p className="text-[15.5px] leading-[1.6] text-navy font-semibold italic">
                    &ldquo;{CASE_STUDIES[openIdx].quote}&rdquo;
                  </p>
                  <div className="mt-3 text-[11.5px] text-navy/55 font-mono uppercase tracking-[0.1em] font-bold">
                    {CASE_STUDIES[openIdx].author}
                  </div>
                </div>
                <div className="space-y-5">
                  {CASE_STUDIES[openIdx].story.map((para, i) => (
                    <p key={i} className="text-[15px] leading-[1.7] text-navy/80 font-medium">{para}</p>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-line flex flex-col sm:flex-row gap-3">
                  <Link to="/lender/signup" onClick={() => setOpenIdx(null)} data-testid="case-study-signup"
                    className="btn-shine group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald flex-1">
                    Apply to join the panel <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
                  </Link>
                  <button onClick={() => setOpenIdx(null)}
                    className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-line text-navy/70 font-bold hover:bg-paper-warm text-[13.5px]">
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Panel enquiry (chat-first) modal */}
      <LenderChatModal open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}

function LenderChatModal({ open, onClose }) {
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", products: "", ticket_size: "", best_time: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error("Company, name and email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/lenders/enquiry", form);
      setSent(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const close = () => {
    setSent(false);
    setForm({ company: "", name: "", email: "", phone: "", products: "", ticket_size: "", best_time: "", message: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl bg-white border-line rounded-[24px] p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="lender-chat-modal">
        <VisuallyHidden>
          <DialogTitle>Talk to the SwiftCash panel team</DialogTitle>
          <DialogDescription>Send a panel enquiry — we reply within 24 hours.</DialogDescription>
        </VisuallyHidden>
        <div className="bg-navy text-white p-7 md:p-8 relative overflow-hidden shrink-0">
          <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <button onClick={close} aria-label="Close" data-testid="lender-chat-close"
            className="absolute top-4 right-4 h-8 w-8 rounded-full border border-white/20 text-white/80 hover:bg-white/10 flex items-center justify-center">
            <X className="h-4 w-4" strokeWidth={2.4} />
          </button>
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-4">
              <MessageCircle className="h-3 w-3" strokeWidth={2.6} /> Panel enquiry
            </div>
            <h2 className="font-display font-extrabold text-[26px] md:text-[32px] tracking-[-0.03em] leading-[1.1] text-white mb-2">
              {sent ? "Thanks — we'll be in touch." : "Talk to the SwiftCash panel team."}
            </h2>
            <p className="text-[13.5px] text-white/70 font-medium leading-[1.55]">
              {sent
                ? "Your enquiry is with our panel desk. We reply to every message within 24 hours (Mon–Fri)."
                : "Tell us about your lending appetite and we'll walk you through the panel before you sign up."}
            </p>
          </div>
        </div>

        {sent ? (
          <div className="p-7 md:p-8 flex flex-col gap-4">
            <div className="rounded-xl bg-emerald-soft/60 border border-emerald/20 p-5">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-emerald text-white flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </div>
                <div>
                  <div className="font-display font-extrabold text-[15px] text-navy mb-1">Enquiry sent</div>
                  <div className="text-[13px] text-navy/70 leading-[1.55]">
                    A panel manager will reach you at <span className="font-mono font-bold text-navy">{form.email}</span>.
                  </div>
                </div>
              </div>
            </div>
            <button onClick={close} data-testid="lender-chat-done"
              className="h-12 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-8 overflow-y-auto space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <ChatField label="Company *" value={form.company} onChange={set("company")} testid="lender-chat-company" />
              <ChatField label="Your name *" value={form.name} onChange={set("name")} testid="lender-chat-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <ChatField label="Work email *" type="email" value={form.email} onChange={set("email")} testid="lender-chat-email" />
              <ChatField label="Phone" value={form.phone} onChange={set("phone")} testid="lender-chat-phone" />
            </div>
            <ChatField label="Products you fund" placeholder="e.g. Working capital, asset finance" value={form.products} onChange={set("products")} testid="lender-chat-products" />
            <div className="grid sm:grid-cols-2 gap-3">
              <ChatField label="Typical ticket size" placeholder="£25k – £500k" value={form.ticket_size} onChange={set("ticket_size")} testid="lender-chat-ticket" />
              <ChatField label="Best time to call" placeholder="Weekday morning" value={form.best_time} onChange={set("best_time")} testid="lender-chat-best-time" />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-[0.12em] font-bold text-navy/55 mb-1.5">Anything specific to discuss?</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={set("message")}
                data-testid="lender-chat-message"
                className="w-full rounded-lg border border-line bg-paper-warm px-3.5 py-2.5 text-[14px] text-navy placeholder:text-navy/35 focus:outline-none focus:border-emerald focus:bg-white transition-colors resize-none"
                placeholder="Sector focus, ticket range, exclusions, timelines…"
              />
            </div>
            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={busy}
                data-testid="lender-chat-submit"
                className="btn-shine group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald flex-1 disabled:opacity-60"
              >
                {busy ? "Sending…" : (<>Send enquiry <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} /></>)}
              </button>
              <button type="button" onClick={close}
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-line text-navy/70 font-bold hover:bg-paper-warm text-[13.5px]">
                Cancel
              </button>
            </div>
            <p className="text-[11px] text-navy/45 font-mono uppercase tracking-[0.1em] font-bold pt-1">
              We reply within 24h · No data shared outside SwiftCash
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChatField({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] font-bold text-navy/55 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-lg border border-line bg-paper-warm px-3.5 h-11 text-[14px] text-navy placeholder:text-navy/35 focus:outline-none focus:border-emerald focus:bg-white transition-colors"
      />
    </div>
  );
}

function ModalStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-center min-w-0 overflow-hidden">
      <div
        className="font-display font-extrabold text-[14px] sm:text-[15px] tracking-[-0.02em] leading-[1.15] break-words hyphens-auto text-white"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-white/55 mt-1.5 font-bold">{label}</div>
    </div>
  );
}

function MicroStat({ label, value }) {
  return (
    <div className="rounded-lg bg-emerald-soft/60 px-2 py-2.5 text-center flex flex-col justify-center min-h-[64px] min-w-0 overflow-hidden">
      <div
        className="font-display font-extrabold text-[12px] sm:text-[12.5px] text-navy tracking-[-0.01em] leading-[1.15] break-words hyphens-auto"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-navy/50 mt-1.5 font-bold">{label}</div>
    </div>
  );
}
